import {
  alpha,
  createTheme as muiCreateTheme,
  darken
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
export * from "@mui/material/styles";
const defaultFontFamily = [
  '"Source Sans Pro"',
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"'
].join(",");
const baseTheme = muiCreateTheme();
const themeWithPrimitives = muiCreateTheme(
  deepmerge(baseTheme, {
    palette: {
      default: {
        main: baseTheme.palette.grey[500],
        light: baseTheme.palette.grey[400],
        dark: baseTheme.palette.grey[900],
        contrastText: baseTheme.palette.common.white
      },
      neutral: {
        main: baseTheme.palette.grey[800],
        light: baseTheme.palette.grey[400],
        dark: baseTheme.palette.grey[900],
        contrastText: baseTheme.palette.common.white
      }
    },
    typography: {
      fontFamily: defaultFontFamily,
      h1: {
        fontFamily: defaultFontFamily,
        fontWeight: 300,
        fontSize: "6rem",
        lineHeight: "7.002rem",
        letterSpacing: "0.15px"
      },
      h2: {
        fontFamily: defaultFontFamily,
        fontWeight: 300,
        fontSize: "3.75rem",
        lineHeight: "4.5rem",
        letterSpacing: "0.15px"
      },
      h3: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "3rem",
        lineHeight: "3.501rem",
        letterSpacing: "0.25px"
      },
      h4: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "2.125rem",
        lineHeight: "2.624rem",
        letterSpacing: "0.25px"
      },
      h5: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "1.5rem",
        lineHeight: "2.001rem",
        letterSpacing: "0.15px"
      },
      h6: {
        fontFamily: defaultFontFamily,
        fontWeight: 500,
        fontSize: "1.25rem",
        lineHeight: "2rem",
        letterSpacing: "0.15px"
      },
      subtitle1: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.75rem",
        letterSpacing: "0.15px"
      },
      subtitle2: {
        fontFamily: defaultFontFamily,
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: "1.374rem",
        letterSpacing: "0.1px"
      },
      body1: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        letterSpacing: "0.15px"
      },
      body2: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.251rem",
        letterSpacing: "0.17px"
      },
      caption: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "1.245rem",
        letterSpacing: "0.4px"
      },
      button: {
        fontFamily: defaultFontFamily,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        letterSpacing: "0.5px",
        textTransform: "none"
      },
      overline: {
        fontFamily: defaultFontFamily,
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "1.995rem",
        letterSpacing: "1px",
        textTransform: "uppercase"
      }
    },
    sizes: {
      avatar: {
        xsmall: { width: "24px", height: "24px", fontSize: "14px" },
        small: { width: "32px", height: "32px", fontSize: "16px" },
        medium: { width: "40px", height: "40px", fontSize: "18px" },
        large: { width: "72px", height: "72px", fontSize: "28px" },
        xlarge: { width: "96px", height: "96px", fontSize: "48px" }
      }
    }
  })
);
const theme = muiCreateTheme(
  deepmerge(
    {
      // Add theme customizations here
      components: {
        MuiAccordion: {
          defaultProps: {
            elevation: 1
          }
        },
        MuiAccordionActions: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontWeight: currentTheme.typography.fontWeightMedium,
              fontSize: currentTheme.typography.body1.fontSize,
              lineHeight: currentTheme.typography.body1.lineHeight,
              letterSpacing: currentTheme.typography.body1.letterSpacing
            })
          }
        },
        MuiAccordionDetails: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontWeight: currentTheme.typography.fontWeightMedium,
              fontSize: currentTheme.typography.body1.fontSize,
              lineHeight: currentTheme.typography.body1.lineHeight,
              letterSpacing: currentTheme.typography.body1.letterSpacing
            })
          }
        },
        MuiAccordionSummary: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontFamily: currentTheme.typography.fontFamily,
              fontWeight: currentTheme.typography.fontWeightBold,
              fontSize: currentTheme.typography.body1.fontSize,
              lineHeight: currentTheme.typography.body1.lineHeight,
              letterSpacing: currentTheme.typography.body1.letterSpacing,
              "&:is(:focus, .Mui-focusVisible)": {
                backgroundColor: currentTheme.palette.action.focus
              }
            }),
            content: ({ theme: currentTheme }) => ({
              flexDirection: "column",
              gap: currentTheme.spacing(0.5)
            }),
            primaryText: ({ theme: currentTheme }) => ({
              fontWeight: currentTheme.typography.fontWeightBold
            })
          }
        },
        MuiAlert: {
          defaultProps: {
            variant: "filled",
            severity: "neutral"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              alignItems: "flex-start",
              display: "grid",
              gridTemplateAreas: '"icon message action"',
              gridTemplateColumns: "auto 1fr auto",
              padding: "".concat(currentTheme.spacing(1.5), " ").concat(currentTheme.spacing(
                3
              ))
            }),
            outlined: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.text.primary,
              "& .MuiButton-root:not(:disabled, .disabled)": {
                color: currentTheme.palette.primary.main
              }
            }),
            filled: () => ({
              "& .MuiButton-root:not(:disabled, .disabled)": {
                color: "inherit"
              }
            }),
            icon: ({ theme: currentTheme }) => ({
              gridArea: "icon",
              fontSize: currentTheme.typography.h5.fontSize
            }),
            message: ({ theme: currentTheme }) => ({
              gridArea: "message",
              display: "flex",
              flexFlow: "column nowrap",
              gap: currentTheme.spacing(0.75),
              fontSize: currentTheme.typography.body1.fontSize,
              fontWeight: currentTheme.typography.fontWeightRegular
            }),
            action: ({ theme: currentTheme }) => ({
              gridArea: "action",
              alignItems: "center",
              padding: currentTheme.spacing(0, 0, 0, 2),
              columnGap: currentTheme.spacing(1.5),
              "&:not(:disabled, .disabled)": {
                color: "inherit"
              },
              "& .MuiButton-root": {
                "&:hover, &.hover": {
                  backgroundColor: "color-mix(in srgb, currentColor 10%, transparent)",
                  borderColor: "color-mix(in srgb, currentColor 10%, transparent)"
                },
                "&:focus, &.focus": {
                  borderColor: "currentColor",
                  boxShadow: "0 0 0 1px currentColor"
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: "color-mix(in srgb, currentColor 20%, transparent)",
                  borderColor: "currentColor"
                },
                "&:disabled, &.disabled": {
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  boxShadow: "none"
                }
              }
            })
          }
        },
        MuiAlertTitle: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              margin: 0,
              fontWeight: currentTheme.typography.fontWeightBold
            })
          }
        },
        MuiAutocomplete: {
          defaultProps: {
            size: "medium"
          },
          styleOverrides: {
            root: ({ ownerState }) => ({
              width: ownerState.fullWidth ? "100%" : "fit-content",
              "& .MuiAutocomplete-endAdornment": {
                right: 12
              },
              "& .MuiFormControl-root": {
                width: "100%"
              }
            })
          }
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: "rgba(0,0,0,0.32)"
            }
          }
        },
        MuiBadge: {
          styleOverrides: {
            // Not the most ideal name for this color, but it is what UX is calling it
            colorSecondary: ({ theme: currentTheme }) => ({
              backgroundColor: currentTheme.palette.grey[500]
            })
          }
        },
        MuiBottomNavigationAction: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.text.primary,
              "&:focus, &.focus": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2),
                "&.Mui-selected": {
                  backgroundColor: alpha(
                    currentTheme.palette.primary.main,
                    0.1
                  )
                }
              }
            }),
            label: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.body2.fontSize
            })
          }
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
            disableFocusRipple: true,
            disableRipple: true,
            disableTouchRipple: true
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              borderColor: "transparent",
              borderStyle: "solid",
              borderWidth: "1px",
              padding: currentTheme.spacing(1, 2),
              "&:disabled, &.disabled": {
                color: currentTheme.palette.action.disabled
              },
              // For instances where the button is supposed to look disabled but is not
              // (e.g. has a popover with an explanation for why it is disabled)
              "&.disabled": {
                pointerEvents: "auto"
              },
              "&:focus, &.focus": {
                outlineWidth: "2px",
                outlineStyle: "solid",
                outlineColor: "transparent"
              }
            }),
            contained: ({ theme: currentTheme }) => ({
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.common.white
              },
              borderColor: currentTheme.palette.primary.main,
              backgroundColor: currentTheme.palette.primary.main,
              "&:hover, &:focus, &.hover, &.focus": {
                borderColor: darken(currentTheme.palette.primary.main, 0.2),
                backgroundColor: darken(currentTheme.palette.primary.main, 0.2)
              },
              "&:focus, &.focus": {
                borderColor: currentTheme.palette.common.white,
                boxShadow: "0 0 0 1px ".concat(currentTheme.palette.common.white, ", 0 0 0 3px ").concat(darken(currentTheme.palette.primary.main, 0.2))
              },
              "&:active, &.active": {
                boxShadow: "none",
                backgroundColor: darken(currentTheme.palette.primary.main, 0.4),
                borderColor: darken(currentTheme.palette.primary.main, 0.4)
              },
              "&:disabled, &.disabled": {
                borderColor: currentTheme.palette.common.white,
                backgroundColor: currentTheme.palette.grey[100],
                boxShadow: "none"
              }
            }),
            text: ({ theme: currentTheme }) => ({
              "&:not(:disabled, .disabled)": {
                color: currentTheme.palette.primary.main
              },
              "&:hover, &.hover": {
                backgroundColor: alpha(currentTheme.palette.primary.light, 0.1),
                borderColor: alpha(currentTheme.palette.primary.light, 0.1)
              },
              "&:focus, &.focus": {
                borderColor: currentTheme.palette.grey[900],
                boxShadow: "0 0 0 1px ".concat(currentTheme.palette.grey[900])
              },
              "&:active, &.active": {
                boxShadow: "none",
                backgroundColor: alpha(currentTheme.palette.primary.light, 0.2),
                borderColor: currentTheme.palette.primary.dark
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: "transparent",
                boxShadow: "none"
              }
            }),
            outlinedPrimary: ({ theme: currentTheme }) => ({
              borderColor: currentTheme.palette.primary.main,
              "&:hover, &.hover": {
                backgroundColor: alpha(currentTheme.palette.primary.light, 0.1)
              },
              "&:focus, &.focus": {
                borderColor: currentTheme.palette.grey[900],
                boxShadow: "0 0 0 1px ".concat(currentTheme.palette.grey[900])
              },
              "&:active, &.active": {
                backgroundColor: alpha(currentTheme.palette.primary.light, 0.2),
                borderColor: currentTheme.palette.primary.dark,
                boxShadow: "none"
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: currentTheme.palette.grey[200],
                boxShadow: "none"
              }
            }),
            outlined: ({ theme: currentTheme }) => ({
              "&.MuiButton-outlinedDefault": {
                borderColor: currentTheme.palette.grey[400],
                "&:hover, &.hover": {
                  backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
                },
                "&:focus, &.focus": {
                  borderColor: currentTheme.palette.grey[900],
                  boxShadow: "0 0 0 1px ".concat(currentTheme.palette.grey[900])
                },
                "&:active, &.active": {
                  boxShadow: "none",
                  backgroundColor: alpha(currentTheme.palette.grey[500], 0.2),
                  borderColor: currentTheme.palette.grey[900]
                },
                "&:not(:disabled, .disabled)": {
                  color: currentTheme.palette.common.black
                },
                "&:disabled, &.disabled": {
                  boxShadow: "none",
                  borderColor: currentTheme.palette.grey[200],
                  backgroundColor: "transparent"
                }
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: currentTheme.palette.grey[200],
                boxShadow: "none"
              }
            })
          }
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
            disableTouchRipple: true
          }
        },
        MuiButtonGroup: {
          defaultProps: {
            disableElevation: true,
            disableFocusRipple: true,
            disableRipple: true,
            color: "default",
            size: "medium",
            variant: "outlined"
          },
          styleOverrides: {
            grouped: ({ theme: currentTheme }) => ({
              "&.MuiButton-root": {
                "&:focus, &.focus": {
                  boxShadow: "inset 0 0 0 1px ".concat(currentTheme.palette.grey[900]),
                  outline: "2px solid transparent",
                  outlineOffset: "-2px"
                },
                "&:disabled, &.disabled": {
                  borderColor: "".concat(currentTheme.palette.grey[400])
                }
              }
            }),
            groupedHorizontal: ({ theme: currentTheme }) => ({
              /* Need the border of the next button in the group to match */
              "&:focus, &.focus, &:active, &.active": {
                "& + .MuiButton-root": {
                  borderLeftColor: "".concat(currentTheme.palette.grey[900])
                }
              }
            }),
            groupedVertical: ({ theme: currentTheme }) => ({
              /* Need the border of the next button in the group to match */
              "&:focus, &.focus, &:active, &.active": {
                "& + .MuiButton-root": {
                  borderTopColor: "".concat(currentTheme.palette.grey[900])
                }
              }
            })
          }
        },
        MuiCardActions: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            })
          }
        },
        MuiCardHeader: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            }),
            action: () => ({
              margin: 0
            })
          }
        },
        MuiCardContent: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            })
          }
        },
        MuiCardMedia: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(2)
            })
          }
        },
        MuiCheckbox: {
          defaultProps: {
            color: "default"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: 12,
              borderRadius: currentTheme.shape.borderRadius,
              "&:hover": {
                backgroundColor: currentTheme.palette.action.hover,
                backgroundClip: "content-box"
              },
              "&:focus-within > .MuiSvgIcon-root": {
                borderRadius: currentTheme.shape.borderRadius,
                outline: "2px solid transparent",
                outlineOffset: -2,
                outlineColor: currentTheme.palette.text.primary
              },
              "&.Mui-error, &.MuiCheckbox-colorError": {
                color: currentTheme.palette.error.main
              }
            }),
            disabled: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.default.light
            })
          }
        },
        MuiChip: {
          defaultProps: {
            variant: "outlined"
          },
          styleOverrides: {
            root: ({ theme: currentTheme, ownerState }) => {
              var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
              const colorObj = currentTheme.palette[ownerState.color];
              return {
                position: "relative",
                color: currentTheme.palette.text.primary,
                backgroundColor: alpha(colorObj.main, 0.1),
                border: "1px solid ".concat(colorObj.main),
                // Increase click/touch target for clickable chips
                "&.MuiChip-clickable:after": {
                  content: '""',
                  position: "absolute",
                  top: "-6px",
                  bottom: "-6px",
                  left: "0",
                  right: "calc(2em + 6px)"
                },
                // Increase click/touch target for delete icon for deletable chips
                "&.MuiChip-deletable .MuiChip-deleteIcon": {
                  padding: "10px",
                  margin: "-10px -5px -10px -6px"
                },
                ["& .MuiSvgIcon-root:not(.MuiAvatar-fallback)"]: {
                  color: alpha(colorObj.main, 0.7)
                },
                ["& .MuiChip-avatarColor".concat(((_b = (_a = ownerState.color) == null ? void 0 : _a.charAt(0).toUpperCase()) != null ? _b : "") + ((_d = (_c = ownerState.color) == null ? void 0 : _c.slice(1)) != null ? _d : ""))]: {
                  color: colorObj.contrastText,
                  backgroundColor: alpha(colorObj.main, 0.7)
                },
                ["&:is(.MuiChip-clickable, .MuiChip-deletable).MuiChip-color".concat(((_f = (_e = ownerState.color) == null ? void 0 : _e.charAt(0).toUpperCase()) != null ? _f : "") + ((_h = (_g = ownerState.color) == null ? void 0 : _g.slice(1)) != null ? _h : ""), ":is(:hover,.hover,:focus,.focus)")]: {
                  backgroundColor: alpha(colorObj.main, 0.4)
                },
                ["&.MuiChip-clickable.MuiChip-color".concat(((_j = (_i = ownerState.color) == null ? void 0 : _i.charAt(0).toUpperCase()) != null ? _j : "") + ((_l = (_k = ownerState.color) == null ? void 0 : _k.slice(1)) != null ? _l : ""), ":is(:active,.active)")]: {
                  boxShadow: currentTheme.shadows[1]
                }
              };
            }
          }
        },
        MuiCircularProgress: {
          defaultProps: {
            color: "primary",
            variant: "indeterminate"
          }
        },
        MuiFormControl: {
          defaultProps: {
            color: "default"
          },
          variants: [
            {
              props: { color: "default" },
              style: {
                color: themeWithPrimitives.palette.default.dark
              }
            }
          ]
        },
        MuiFormControlLabel: {
          styleOverrides: {
            label: {
              fontSize: 16
            },
            disabled: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.default.light
            }),
            error: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.error.main
            })
          }
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 4,
              margin: 0,
              fontSize: 16,
              color: currentTheme.palette.text.primary
            }),
            disabled: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.default.light
            }),
            error: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.error.main
            })
          }
        },
        MuiFormLabel: {
          defaultProps: {
            color: "default"
          },
          variants: [
            {
              props: { color: "default" },
              style: {
                color: themeWithPrimitives.palette.default.dark
              }
            }
          ],
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontSize: 18,
              fontWeight: 600,
              "&.Mui-focused": {
                color: currentTheme.palette.default.dark
              }
            }),
            disabled: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.default.light
            }),
            error: ({ theme: currentTheme }) => ({
              color: currentTheme.palette.error.main
            })
          }
        },
        MuiIconButton: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              position: "relative",
              borderRadius: currentTheme.shape.borderRadius,
              borderColor: "transparent",
              borderStyle: "solid",
              borderWidth: "1px",
              transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              "&:not(:disabled, .disabled)": {
                color: "inherit"
              },
              "&:hover, &.hover": {
                backgroundColor: "color-mix(in srgb, currentColor 10%, transparent)",
                borderColor: "color-mix(in srgb, currentColor 10%, transparent)"
              },
              "&:focus, &.focus": {
                outlineWidth: "2px",
                outlineStyle: "solid",
                outlineColor: "transparent",
                borderColor: "currentColor",
                boxShadow: "0 0 0 1px currentColor"
              },
              "&:active, &.active": {
                boxShadow: "none",
                backgroundColor: "color-mix(in srgb, currentColor 20%, transparent)",
                borderColor: "currentColor"
              },
              "&:disabled, &.disabled": {
                backgroundColor: "transparent",
                borderColor: "transparent",
                boxShadow: "none"
              },
              "&:after": {
                content: '""',
                position: "absolute",
                top: currentTheme.spacing(-0.5),
                bottom: currentTheme.spacing(-0.5),
                left: currentTheme.spacing(-0.5),
                right: currentTheme.spacing(-0.5)
              }
            })
          }
        },
        MuiInputBase: {
          defaultProps: {
            color: "default"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(0),
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: currentTheme.palette.default.main,
              borderRadius: currentTheme.shape.borderRadius,
              "&.MuiInputBase-adornedStart": {
                paddingLeft: currentTheme.spacing(1)
              },
              "&.MuiInputBase-adornedEnd": {
                paddingRight: currentTheme.spacing(1)
              },
              "&:hover:not(.Mui-disabled)": {
                borderColor: currentTheme.palette.default.dark
              },
              "&:focus-within, &.Mui-focused": {
                outline: "1px solid",
                outlineColor: currentTheme.palette.default.dark,
                outlineOffset: -2
              },
              "&.Mui-error": {
                borderColor: currentTheme.palette.error.main,
                outline: "1px solid",
                outlineColor: currentTheme.palette.error.main,
                outlineOffset: -2
              },
              "&:disabled": {
                borderColor: currentTheme.palette.default.light,
                color: currentTheme.palette.default.light
              }
            }),
            input: ({ theme: currentTheme, ownerState }) => ({
              padding: currentTheme.spacing(
                2,
                1.75,
                ownerState.multiline ? 0 : 2,
                1.75
              ),
              "&.MuiInputBase-inputSizeSmall": {
                padding: currentTheme.spacing(
                  1,
                  1.75,
                  ownerState.multiline ? 0 : 1,
                  1.75
                )
              }
            })
          }
        },
        MuiInputLabel: {
          defaultProps: {
            color: "default",
            variant: "outlined"
          },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              fontSize: 18,
              fontWeight: currentTheme.typography.fontWeightBold
            })
          }
        },
        MuiLinearProgress: {
          defaultProps: {
            color: "primary",
            variant: "indeterminate"
          },
          styleOverrides: {
            root: {
              width: "100%"
            }
          }
        },
        MuiLink: {
          defaultProps: {
            tabIndex: 0
          },
          styleOverrides: {
            root: {
              /**
               * Browsers handle the focus outline differently, even across different operating systems.
               * For instance, Firefox and Safari on MacOS will not even allow tabbing to links without explicitly
               * enabling the setting in the browser or the OS. Once enabled, the focus outline is controlled by
               * the user agent styles.
               * The overall size of the outline is largely driven by the font-size and line-height (whichever
               * is larger).
               * The outline-offset below ensures that there is at least a 2px gap between the outline and the
               * text.
               */
              "&:is(.Mui-focusVisible,:focus,:focus-visible)": {
                textDecoration: "none",
                outlineOffset: "2px"
              }
            }
          }
        },
        MuiMenu: {
          styleOverrides: {
            root: {
              ".MuiBackdrop-root": {
                backgroundColor: "transparent"
              }
            }
          }
        },
        MuiMenuItem: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              "&:hover": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
              },
              // Apply to focus instead of focus-visible because MUI auto focuses it
              // on menu trigger which does not apply :focus-visible
              "&.Mui-focusVisible,&:focus": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.2)
              },
              "&.Mui-selected": {
                backgroundColor: alpha(currentTheme.palette.grey[400], 0.4),
                // Combat Mui re-applying primary to these states.
                "&:hover": {
                  backgroundColor: alpha(currentTheme.palette.grey[400], 0.4)
                },
                "&.Mui-focusVisible,&:focus": {
                  backgroundColor: alpha(currentTheme.palette.grey[400], 0.6)
                }
              }
            })
          }
        },
        MuiMobileStepper: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              padding: currentTheme.spacing(1, 0)
            })
          }
        },
        MuiPagination: {
          defaultProps: {
            color: "standard",
            size: "large"
          }
        },
        MuiRadio: {
          defaultProps: { color: "default" },
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              borderRadius: currentTheme.shape.borderRadius,
              "&:hover": {
                backgroundColor: currentTheme.palette.action.hover,
                backgroundClip: "content-box"
              },
              "&.MuiRadio-colorError": {
                color: currentTheme.palette.error.main
              },
              "&:focus-within .MuiSvgIcon-root:first-of-type": {
                outlineWidth: "2px",
                outlineOffset: "-1px",
                outlineStyle: "solid",
                outlineColor: currentTheme.palette.text.primary
              }
            })
          }
        },
        MuiSnackbar: {
          styleOverrides: {
            root: () => ({
              // UX does not want the snackbars to be difference sizes based on whether or no they have a button.
              // Set the min height to to account for a button
              minHeight: "42px"
            })
          }
        },
        MuiSnackbarContent: {
          styleOverrides: {
            root: ({ theme: currentTheme }) => ({
              paddingTop: currentTheme.spacing(0.25),
              paddingBottom: currentTheme.spacing(0.25)
            })
          }
        },
        MuiStep: {
          styleOverrides: {
            root: {
              "&.MuiStep-vertical.MuiStep-alternativeLabel": {
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }
            }
          }
        },
        MuiStepConnector: {
          styleOverrides: {
            root: {
              "&.MuiStepConnector-vertical.MuiStepConnector-alternativeLabel": {
                marginLeft: "initial",
                position: "initial",
                top: "unset",
                left: "unset",
                right: "unset"
              }
            }
          }
        },
        MuiStepper: {
          styleOverrides: {
            root: {
              "&.MuiStepper-vertical.MuiStepper-alternativeLabel": {
                alignItems: "center",
                width: "fit-content"
              }
            }
          }
        },
        MuiSvgIcon: {
          variants: [
            {
              props: { fontSize: "inherit" },
              style: {
                fontSize: "inherit"
              }
            },
            {
              props: { fontSize: "small" },
              style: {
                fontSize: "1.25rem"
              }
            },
            {
              props: { fontSize: "medium" },
              style: {
                fontSize: "1.5rem"
              }
            },
            {
              props: { fontSize: "large" },
              style: {
                fontSize: "2rem"
              }
            },
            {
              props: { fontSize: "xlarge" },
              style: {
                fontSize: "3rem"
              }
            }
          ]
        },
        MuiTableCell: {
          styleOverrides: {
            head: ({ theme: currentTheme }) => ({
              fontWeight: currentTheme.typography.fontWeightBold
            })
          }
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: "none",
              "&:is(:focus,.Mui-focusVisible)": [
                () => "\n                outline: 2px auto;\n                outline-color: Highlight;\n                outline-color: -webkit-focus-ring-color;\n                outline-offset: -0.5rem;\n                border-radius: 2px;\n              "
              ]
            }
          }
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              ".MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3
              }
            }
          }
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({ theme: currentTheme }) => ({
              fontSize: currentTheme.typography.htmlFontSize
            })
          }
        }
      }
    },
    themeWithPrimitives
  )
);
export {
  theme
};
